import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { ensureSharedPusher } from '@/services/sharedPusherClient'
import useCurrentUser from '@/hooks/useCurrentUser'
import { fetchOrderBook, fetchMarketWagersByMarketId } from '@/store/markets'
import { fetchUserOrders, fetchQuestionTransactions } from '@/store/wagers'
import { fetchCurrentUser } from '@/store/account'

/**
 * Centralized Pusher Order Book Real-Time Updates
 *
 * This module provides a singleton-based approach to managing Pusher subscriptions
 * for order book updates. It prevents duplicate event handlers and API calls
 * that were occurring when multiple components subscribed to the same channels.
 *
 * Key features:
 * - Uses `sharedPusherClient` (shared with `useRealTimeMarket`) so one WebSocket is used
 * - One `event-{eventId}` channel per event (question); `order-book-update` is
 *   emitted per market (outcome) with `market_id`, `event_id`, and `currency_mode`
 * - On each update, refetch the full REST order book for matching subscriptions
 *   (top-of-book payload is not applied inline)
 * - One event handler per channel regardless of how many components subscribe
 * - Automatic deduplication of order book fetch requests
 * - Proper cleanup when components unmount
 * - Reference counting for channel subscriptions
 *
 * Usage:
 * useOrderBookRealTime([
 *   {
 *     marketId: 'event-123', // event (question) id — channel key
 *     currency: 'OOM', // or 'USD', 'BTC', etc.
 *     outcome: 'outcome-456', // market (outcome) id
 *     positionType: 'long' // 'long' | 'short' (required)
 *   }
 * ])
 */

// Singleton Pusher connection manager
class OrderBookPusherManager {
  constructor() {
    this.pusher = null
    this.userId = null
    this.channels = new Map()
    this.subscriptions = new Map() // channelId -> Set of subscription keys
    this.subscriptionDetails = new Map() // subscriptionKey -> {outcome, positionType}
    this.subscriptionRefCount = new Map() // subscriptionKey -> count of components using it
    this.dispatch = null
  }

  async initialize(dispatch, userId) {
    if (import.meta.env.NODE_ENV === 'test') {
      return
    }

    if (!import.meta.env.VITE_PUSHER_APP_KEY || !import.meta.env.VITE_PUSHER_APP_CLUSTER) {
      return
    }

    this.dispatch = dispatch
    this.userId = userId
    this.pusher = await ensureSharedPusher(userId ?? null)
  }

  subscribe(subscription) {
    if (!this.pusher || !subscription || !subscription.marketId || !subscription.currency || !subscription.outcome || !subscription.positionType) {
      return () => {} // Return empty cleanup function
    }

    const { marketId, currency, outcome, positionType } = subscription
    const channelId = `event-${marketId}`
    const currencyMode = currency === 'OOM' ? 'play_money' : 'real_money'

    // Create unique subscription key including currency mode and position
    const subscriptionKey = `${channelId}-${currencyMode}-${outcome}-${positionType}`

    // Initialize subscriptions set for this channel if it doesn't exist
    if (!this.subscriptions.has(channelId)) {
      this.subscriptions.set(channelId, new Set())
    }

    // Add this specific subscription with reference counting
    const currentRefCount = this.subscriptionRefCount.get(subscriptionKey) || 0
    this.subscriptionRefCount.set(subscriptionKey, currentRefCount + 1)

    // Only add to channel subscriptions if this is the first reference
    if (currentRefCount === 0) {
      this.subscriptions.get(channelId).add(subscriptionKey)
      this.subscriptionDetails.set(subscriptionKey, { outcome, position: positionType, currencyMode })
    }

    // Only create channel if it doesn't exist
    if (!this.channels.has(channelId)) {
      const channel = this.pusher.subscribe(channelId)
      this.channels.set(channelId, channel)

      channel.bind('order-book-update', data => {
        this.handleOrderBookUpdate(channelId, data)
      })
    }

    // Return cleanup function
    return () => {
      this.unsubscribe(channelId, subscriptionKey)
    }
  }

  unsubscribe(channelId, subscriptionKey) {
    const currentRefCount = this.subscriptionRefCount.get(subscriptionKey) || 0
    
    if (currentRefCount > 1) {
      // Decrement reference count but keep the subscription active
      this.subscriptionRefCount.set(subscriptionKey, currentRefCount - 1)
    } else if (currentRefCount === 1) {
      // Last reference - remove the subscription completely
      this.subscriptionRefCount.delete(subscriptionKey)
      this.subscriptionDetails.delete(subscriptionKey)
      
      const channelSubscriptions = this.subscriptions.get(channelId)
      if (channelSubscriptions) {
        channelSubscriptions.delete(subscriptionKey)

        // If no more subscriptions for this channel, clean up the channel
        if (channelSubscriptions.size === 0) {
          this.subscriptions.delete(channelId)
          
          const channel = this.channels.get(channelId)
          if (channel) {
            this.pusher.unsubscribe(channelId)
            this.channels.delete(channelId)
          }
        }
      }
    }
  }

  handleOrderBookUpdate(channelId, data) {
    if (!this.dispatch || data?.market_id == null || data?.event_id == null) {
      return
    }

    const payloadCurrencyMode =
      data.currency_mode || (data.currency === 'OOM' ? 'play_money' : data.currency ? 'real_money' : null)
    if (!payloadCurrencyMode) {
      return
    }

    const marketId = String(data.market_id)
    const eventId = data.event_id
    const channelSubscriptions = this.subscriptions.get(channelId) || new Set()

    this.dispatch(fetchMarketWagersByMarketId(eventId))
    this.dispatch(fetchCurrentUser())
    this.dispatch(
      fetchUserOrders({
        status: 'open',
        limit: 50,
        clear: true,
        question: eventId,
      })
    )
    this.dispatch(
      fetchQuestionTransactions(
        { id: eventId },
        {
          startIndex: 0,
          limit: 20,
        },
        true
      )
    )

    // Refetch full REST book only for the market (outcome) that changed
    const processedCombinations = new Set()

    channelSubscriptions.forEach(subscriptionKey => {
      const details = this.subscriptionDetails.get(subscriptionKey)
      if (
        details &&
        details.outcome != null &&
        details.position &&
        details.currencyMode === payloadCurrencyMode &&
        String(details.outcome) === marketId
      ) {
        const combinationKey = `${details.outcome}-${details.position}`

        if (!processedCombinations.has(combinationKey)) {
          processedCombinations.add(combinationKey)

          this.dispatch(
            fetchOrderBook({
              question: eventId,
              currency_mode: payloadCurrencyMode,
              outcome: details.outcome,
              position: details.position,
              source: 'realtime_refresh',
            })
          )
        }
      }
    })
  }

  disconnect() {
    if (this.pusher) {
      this.channels.forEach((_, channelId) => {
        try {
          this.pusher.unsubscribe(channelId)
        } catch {
          /* noop */
        }
      })
    }
    this.channels.clear()
    this.subscriptions.clear()
    this.subscriptionDetails.clear()
    this.subscriptionRefCount.clear()
    this.pusher = null
    this.dispatch = null
  }
}

// Global singleton instance
const pusherManager = new OrderBookPusherManager()

/**
 * Hook to subscribe to real-time order book updates via Pusher
 * Uses a centralized subscription manager to prevent duplicate event handlers
 */
const useOrderBookRealTime = (subscriptions = []) => {
  const dispatch = useDispatch()
  const cleanupFunctions = useRef([])
  const currentUser = useCurrentUser()

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      await pusherManager.initialize(dispatch, currentUser?.id)

      if (cancelled) return

      const subscriptionsArray = Array.isArray(subscriptions) ? subscriptions : []

      const validSubscriptions = subscriptionsArray.filter(
        sub => sub && sub.marketId && sub.currency && sub.outcome && sub.positionType
      )

      if (validSubscriptions.length === 0) {
        return
      }

      cleanupFunctions.current = validSubscriptions.map(subscription => pusherManager.subscribe(subscription))
    })()

    return () => {
      cancelled = true
      cleanupFunctions.current.forEach(cleanup => cleanup())
      cleanupFunctions.current = []
    }
  }, [dispatch, currentUser?.id, JSON.stringify(subscriptions)])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupFunctions.current.forEach(cleanup => cleanup())
    }
  }, [])
}

// Export the manager instance for manual control if needed
export const orderBookPusherManager = pusherManager

// Utility function to manually disconnect all subscriptions (useful for testing or app teardown)
export const disconnectOrderBookRealTime = () => {
  pusherManager.disconnect()
}

export default useOrderBookRealTime
